import { sortBy } from "lodash-es";
import z from "zod";
import type { UUID } from "../../utils/uuid";
import type { Brand } from "../brandfetch/types";
import type { BrandFetchChoices } from "./types";

export function brandfetchResultToChoices(result: Brand): BrandFetchChoices {
  return {
    logos: sortLogos(filterLogos(result.logos)),
    colors: result.colors
      .filter((x) => !tooWhite(x.hex))
      .map((color) => ({
        ...color,
        is_default_primary_color: false,
        tag: null,
      })),
  };
}

const formatPriority = ["svg", "png", "jpeg", "jpg"];

const filterLogos = (logos: Brand["logos"]): BrandFetchChoices["logos"] => {
  return logos.map(({ formats, ...rest }) => {
    const format = formats
      .filter((logoFormat) => formatPriority.includes(logoFormat.format))
      .sort((a, b) => {
        const aIndex = formatPriority.indexOf(a.format);
        const bIndex = formatPriority.indexOf(b.format);
        return aIndex - bIndex;
      })[0]!;
    return {
      ...format,
      ...rest,
      background: format.background ?? null,
      is_default_logo: false,
      tag: null,
    };
  });
};

function tooWhite(hex: string): boolean {
  const whiteLimit = 230;

  const r = parseInt("0x" + hex.substring(1, 3));
  const g = parseInt("0x" + hex.substring(3, 5));
  const b = parseInt("0x" + hex.substring(5, 7));
  return !(r < whiteLimit || b < whiteLimit || g < whiteLimit);
}

export function sortLogos(logos: BrandFetchChoices["logos"]): BrandFetchChoices["logos"] {
  const priority = ["icon", "logo", "symbol", "other"];

  return sortBy(logos, (logo) => priority.indexOf(logo.type));
}

const brandKitParser = z.array(
  z.object({
    name: z.string(),
    logo: z.array(z.object({ src: z.string(), id: z.uuidv4().transform((val) => val as UUID) })),
    colors: z.array(z.object({ hex: z.string(), id: z.uuidv4().transform((val) => val as UUID) })),
  }),
);

export const upsertBrandKitJsonToBrandKit = (
  brandKit: unknown,
  brandName: string,
): { logo: { src: string; id: UUID }[]; colors: { hex: string; id: UUID }[] } | undefined => {
  return brandKitParser.safeParse(brandKit).data?.find((x) => x.name === brandName);
};
